import type {
    FeeOverride,
    GenericObject,
    IPencilOfferInputs,
    ManualIncentives,
    Offer,
    OfferType,
    PenciledInputOfferType,
    ResidualInfo
} from '@makemydeal/dr-platform-types';
import type { LenderChangeOrigin, LenderIdentity, ReserveSplitInputs } from '../../types/reserveSplitTypes';
import { isDifferentLender } from '../../utils/reserveSplitUtils';
import { LEASE, RESIDUAL_DOLLAR, RESIDUAL_PERCENT, BALLOON } from '@makemydeal/dr-platform-types';
import type {
    AggregatedIncentives,
    LedgerAwareManualIncentives,
    PencilInputsByOfferType,
    PenciledInputsState,
    PendingLenderSync,
    ProgramPayload
} from '../../types/commonTypes';
import * as selectors from '../../selectors';
import { parseTermId } from '../../utils/termIdParser';
import { isPaymentGridOriginatedAction } from '../../utils/paymentUtils';
import { termHasImplausibleMoneyFactor } from '../../utils/rateUtils';
import { ACQUISITION_FEE_ID } from '../../constants/payment';
import { CALC_ENDPOINTS } from '../../constants/state';
import { PAYMENT_RECEIVED } from '../../types/actionTypes';
import { AppliedIncentiveIdsNode, AppliedIncentivesNode, SelectedIncentivesNode } from '../../constants/penciledInputs';
import {
    buildTermIncentivePriceMap,
    isDealerCash,
    isFrozenProgramRow,
    isGridIncentiveManagementEnabled,
    isUpqRebateLedgerInEffect,
    isNotDealerCash,
    isNotStoredDealerCash,
    sumLedgerRebates,
    isProgramStandardIncentive,
    isStoredDealerCash,
    parseIncentiveId,
    repriceRowsFromPriceMap,
    shouldRederiveAppliedIncentivesForTerm
} from '../../utils/incentives';

export const updateInputWithValue = (prevActiveOffer: GenericObject | undefined, key?: string, value?: any): GenericObject => {
    return {
        ...prevActiveOffer,
        ...(key != null && { [key]: value }),
        isUpdatedByUser: true
    };
};

export const reserveSplitInputsForLenderChange = (
    prevInputs: ReserveSplitInputs | undefined,
    newLender: LenderIdentity,
    origin?: LenderChangeOrigin
): ReserveSplitInputs => {
    if (!isDifferentLender(prevInputs?.creditDecisionLender, newLender)) return {};

    const isClearedByLenderChange = Boolean(prevInputs?.isReserveSplitUserEntered && prevInputs?.reserveSplitOverride != null);

    return {
        reserveSplitOverride: undefined,
        isReserveSplitUserEntered: false,
        isReserveSplitClearedByLenderChange: isClearedByLenderChange,
        reserveSplitClearedFrom: isClearedByLenderChange ? origin : undefined
    };
};

export const updateOfferTypeWithInputs = (byOfferType: any, inputs: any, offerType: OfferType): PencilInputsByOfferType => {
    return {
        ...byOfferType,
        [offerType]: inputs
    };
};

export const updateOfferPrice = (byOfferType: any, offerPrice: any, offerTypes: string[]): PencilInputsByOfferType => {
    const newStateOfferObject: GenericObject = {};

    const composeOfferUpdate = (type: string) => {
        const value = {
            ...byOfferType[type],
            offerPrice
        };
        newStateOfferObject[type] = value;
    };
    offerTypes.forEach(composeOfferUpdate);

    return { ...byOfferType, ...newStateOfferObject };
};

export const resetPenciledInputsOverrides = (original: any, clearManualIncentives = true): PencilInputsByOfferType => {
    let manualIncentiveOverride = {};

    if (clearManualIncentives) {
        manualIncentiveOverride = {
            manualIncentives: undefined
        };
    }

    return {
        finance: {
            ...original.finance,
            acqFeeOverride: undefined,
            acqFeeUpFrontOverride: undefined,
            securityDepositOverride: undefined,
            retailPriceOverride: undefined,
            overridesByTerm: undefined,
            creditDecisionEnabled: undefined,
            creditDecisionLenderName: undefined,
            creditDecisionLender: undefined,
            creditDecisionStatus: undefined,
            incentiveTotalOverride: undefined,
            ...manualIncentiveOverride
        },
        lease: {
            ...original.lease,
            acqFeeOverride: undefined,
            acqFeeUpFrontOverride: undefined,
            securityDepositOverride: undefined,
            retailPriceOverride: undefined,
            overridesByTerm: undefined,
            creditDecisionEnabled: undefined,
            creditDecisionLenderName: undefined,
            creditDecisionLender: undefined,
            creditDecisionStatus: undefined,
            incentiveTotalOverride: undefined,
            excessMileageOverride: undefined,
            maxResidualizedMsrp: undefined,
            maxResidualizedMsrpApplied: undefined,
            ...manualIncentiveOverride
        },
        cash: {
            ...original.cash,
            retailPriceOverride: undefined,
            ...manualIncentiveOverride
        },
        balloon: {
            ...original.balloon,
            securityDepositOverride: undefined,
            retailPriceOverride: undefined,
            overridesByTerm: undefined,
            creditDecisionEnabled: undefined,
            creditDecisionLenderName: undefined,
            creditDecisionLender: undefined,
            creditDecisionStatus: undefined,
            incentiveTotalOverride: undefined,
            excessMileageOverride: undefined,
            ...manualIncentiveOverride
        }
    };
};
export const clearIncentiveSelection = (
    byOfferType: PencilInputsByOfferType,
    preserveSelection: boolean
): PencilInputsByOfferType => {
    return (Object.keys(byOfferType) as PenciledInputOfferType[]).reduce((acc, offerType) => {
        acc[offerType] = {
            ...byOfferType[offerType],
            [SelectedIncentivesNode]: preserveSelection ? byOfferType[offerType]?.[SelectedIncentivesNode] ?? [] : [],
            [AppliedIncentiveIdsNode]: [],
            [AppliedIncentivesNode]: undefined
        };
        return acc;
    }, {} as PencilInputsByOfferType);
};

export const clearFeesOverrides = (byOfferType: PencilInputsByOfferType): PencilInputsByOfferType => {
    return (Object.keys(byOfferType) as PenciledInputOfferType[]).reduce((acc, offerType) => {
        acc[offerType] = {
            ...byOfferType[offerType],
            feesOverride: undefined
        };
        return acc;
    }, {} as PencilInputsByOfferType);
};

/** Lender code recorded on a term record by the payment response, or `undefined` when the term isn't priced yet. */
export const getTermLenderCode = (offer: Offer, termId: string | undefined): string | undefined =>
    termId == null ? undefined : offer?.terms?.byId?.[termId]?.lenderCode;

/** Term length recorded on a term record, which is how per-term overrides are keyed. */
export const getTermMonthsById = (offer: Offer, termId: string | undefined): number | undefined =>
    termId == null ? undefined : offer?.terms?.byId?.[termId]?.months;

/**
 * An identity for the program a term was priced with, for telling "a different program" from "same program, different
 * cell" - the lender code cannot, since one lender offers several programs. `residualProgram` is part of it because on a
 * lease the residual drives the payment.
 */
export const getTermProgramKey = (offer: Offer, termId: string | undefined): string | undefined => {
    if (termId == null) return undefined;
    const lenderProgram = offer?.terms?.byId?.[termId]?.lenderProgram;
    if (!lenderProgram) return undefined;
    const { lenderId, branchId, programType, programName, regionalId, rateProgram, residualProgram } = lenderProgram;
    return [lenderId, branchId, programType, programName, regionalId, rateProgram, residualProgram].join('|');
};

/**
 * Clears the *program-derived* per-term override pinned for one column, so the newly selected program's own values seed
 * it instead of the previous program's.
 *
 * Only `buyRate` is dropped, and that is the whole of it: of the three fields in `TermOverrides`, `sellRate` and
 * `residualInfo` are values the user can enter per column in the grid, and dropping either discarded what they had just
 * typed into the column they were clicking. `buyRate` has no grid input and always comes from the program that priced
 * the term.
 */
export const dropPinnedOverridesForTerm = (inputs: IPencilOfferInputs, months: number | undefined): IPencilOfferInputs => {
    const pinned = months == null ? undefined : inputs.overridesByTerm?.[months];
    if (!pinned) return inputs;

    const { buyRate: _droppedBuyRate, ...kept } = pinned;

    // Nothing of the user's left to keep, so the entry goes whole rather than lingering as `{}`.
    if (!Object.keys(kept).length) {
        const { [months as number]: _dropped, ...remaining } = inputs.overridesByTerm!;
        return { ...inputs, overridesByTerm: remaining };
    }

    return { ...inputs, overridesByTerm: { ...inputs.overridesByTerm, [months as number]: kept } };
};

export const resolveAcqFeeReset = (
    inputs: IPencilOfferInputs | undefined,
    governmentFeesOverride: FeeOverride[] | undefined,
    retain: boolean
): Pick<IPencilOfferInputs, 'acqFeeOverride' | 'acqFeeUpFrontOverride' | 'feesOverride'> => {
    if (!retain) {
        return { acqFeeOverride: undefined, acqFeeUpFrontOverride: undefined, feesOverride: governmentFeesOverride };
    }

    const acqFeeEntry = inputs?.feesOverride?.filter((fee: FeeOverride) => fee?.feeTypeId === ACQUISITION_FEE_ID)[0];

    return {
        acqFeeOverride: inputs?.acqFeeOverride,
        acqFeeUpFrontOverride: inputs?.acqFeeUpFrontOverride,
        feesOverride: acqFeeEntry ? [...(governmentFeesOverride || []), acqFeeEntry] : governmentFeesOverride
    };
};

export const dropProgramDerivedLenderOverrides = (inputs: IPencilOfferInputs): IPencilOfferInputs & PendingLenderSync => {
    const feesOverride = inputs.feesOverride?.filter((fee: FeeOverride) => fee?.feeTypeId !== ACQUISITION_FEE_ID);

    return {
        ...inputs,
        maxResidualizedMsrp: undefined,
        maxResidualizedMsrpApplied: undefined,
        acqFeeOverride: undefined,
        acqFeeUpFrontOverride: undefined,
        feesOverride: feesOverride?.length ? feesOverride : undefined,
        pendingLenderProgramSync: true
    };
};

export const dropManualIncentivesFor = (
    byOfferType: PencilInputsByOfferType,
    dropManualIncentivesForOfferTypes: OfferType[]
): PencilInputsByOfferType => {
    return (Object.keys(byOfferType) as PenciledInputOfferType[]).reduce((acc, offerType) => {
        acc[offerType] = {
            ...byOfferType[offerType],
            manualIncentives: dropManualIncentivesForOfferTypes.includes(offerType)
                ? undefined
                : keepUserManualIncentives(byOfferType[offerType]?.manualIncentives)
        };
        return acc;
    }, {} as PencilInputsByOfferType);
};

export const pushSelectedIncentives = (
    response: AggregatedIncentives,
    name: string,
    conditional: boolean = false
): AggregatedIncentives => {
    const incentiveType = conditional ? 'conditionals' : 'nonConditionals';
    const id = parseIncentiveId(name);
    if (!Number.isNaN(id)) {
        response.allIds.push(id);
    }
    response.allNames.push(name);
    response[incentiveType].push(name);

    return response;
};

export const aggregatedSelectedIncentives = (
    nonConditionals: string[] = [],
    selectedIncentives: string[] = []
): AggregatedIncentives => {
    const response: AggregatedIncentives = { allIds: [], allNames: [], conditionals: [], nonConditionals: [] };

    nonConditionals.forEach((name: string) => pushSelectedIncentives(response, name, false));

    selectedIncentives.forEach((name: string) => {
        if (response.allNames.includes(name)) return;
        pushSelectedIncentives(response, name, true);
    });

    return response;
};

/**
 * Keeps only the user-entered manual incentives (`manual !== false`) and drops program-sourced echoes
 * (`manual === false`), recomputing the dealerCash / totalRebates totals. Mirrors the APPLIED_PROGRAM
 * keepManualIncentives behavior so selecting conditional incentives does not discard the user's manual
 * entries. Returns undefined when no user-entered manual incentives remain.
 *
 * Deliberately does NOT exempt frozen rows the way `shouldRetainManualRow` does in `incentiveHandlers`:
 * every call site here is a vehicle switch or a program (re)apply, where the previous program's frozen
 * rebates must not carry over. `retainFrozenCodes` narrows them to the still-selected codes instead.
 */
export const keepUserManualIncentives = (
    manualIncentives: ManualIncentives | undefined,
    restorationEnabled = false,
    retainFrozenCodes?: Set<number>
): ManualIncentives | undefined => {
    if (!manualIncentives) return undefined;
    const kept = (manualIncentives.incentives || []).filter(
        (incentive) =>
            incentive.manual !== false ||
            (retainFrozenCodes != null && isFrozenProgramRow(incentive) && retainFrozenCodes.has(Number(incentive.code)))
    );
    // An owned bag survives emptying: collapsing it to undefined would make an intentionally empty
    // frozen set indistinguishable from a never-populated one.
    const isLedgerOwned = Boolean((manualIncentives as LedgerAwareManualIncentives).ledgerOwned);
    if (kept.length === 0 && !isLedgerOwned) return undefined;
    const dealerCashFilter = restorationEnabled ? isStoredDealerCash : isDealerCash;
    const rebateFilter = restorationEnabled ? isNotStoredDealerCash : isNotDealerCash;
    const dealerCash = kept.filter(dealerCashFilter).reduce((sum, inc) => sum + (inc.amount || 0), 0);
    const totalRebates = kept.filter(rebateFilter).reduce((sum, inc) => sum + (inc.amount || 0), 0);
    return { ...manualIncentives, incentives: kept, dealerCash, totalRebates };
};

const hasIncentiveCatalog = (term?: GenericObject): boolean =>
    ((term?.incentives?.nonConditionals?.length ?? 0) as number) + ((term?.incentives?.conditionals?.length ?? 0) as number) > 0;

/**
 * DE760840: the term record to price against.
 *
 * Sourced from the payment response's own `payload.terms`, NOT from the merged terms slice. On the
 * restoration-off path `mergeAndReconcileTerms` runs `preserveTermIncentives` with `trustMergedCatalog`
 * false, which replaces the response's fresh per-term catalog with the *previous* term's catalog. Pricing
 * from merged state therefore reads the amount belonging to the term the user just left, the price map
 * matches the amount the row already has, and the re-price no-ops — the defect survives. The payload is the
 * only source that carries the newly selected term's real amounts.
 *
 * Resolution is by exact id, then by an unambiguous match on every param the penciled id asserts. Ids are
 * re-keyed in flight (`lease_term_48` -> `lease_term_48_downpayment_4500_01`), so an exact hit is not
 * guaranteed; matching on params rather than months matters because a lease can carry several terms with the
 * same month count differing by mileage or down payment. A deliberately direct `byId` read rather than
 * `getTermFromOffer`, which applies its own months fallback keyed off `offer.offerType` — that can differ
 * from the `offerType` threaded through here, and it would silently reintroduce the first-month-match guess.
 */
const termParamsSubsumed = (requestedParams: Record<string, number>, candidateId: string): boolean => {
    const candidateParams = parseTermId(candidateId)?.termIdParams;
    if (!candidateParams) {
        return false;
    }
    const keys = Object.keys(requestedParams);
    return keys.length > 0 && keys.every((key) => candidateParams[key] === requestedParams[key]);
};

const findTermInSlice = (termsById: Record<string, GenericObject> | undefined, termId: string): GenericObject | undefined => {
    const exact = termsById?.[termId];
    if (hasIncentiveCatalog(exact)) {
        return exact;
    }
    const requestedParams = parseTermId(termId)?.termIdParams;
    if (!requestedParams) {
        return undefined;
    }
    const matches = Object.keys(termsById ?? {}).filter((candidateId) => termParamsSubsumed(requestedParams, candidateId));
    // Only trust this when it is unambiguous. More than one match means the penciled id does not assert
    // enough to distinguish the variants, and picking one would be a coin flip.
    if (matches.length !== 1) {
        return undefined;
    }
    const byParams = termsById?.[matches[0]];
    return hasIncentiveCatalog(byParams) ? byParams : undefined;
};

const resolveTermForPricing = (action: any, termId: string): GenericObject | undefined =>
    findTermInSlice(action?.payload?.terms?.byId as Record<string, GenericObject> | undefined, termId);

// Uses the stored-shape classifiers: a raw bag row can carry `incentiveType` instead of `type`, which
// `keepUserManualIncentives` also has to allow for.
const sumRebates = (incentives: ManualIncentives['incentives']) => ({
    dealerCash: incentives.filter(isStoredDealerCash).reduce((sum, inc) => sum + (inc.amount || 0), 0),
    totalRebates: sumLedgerRebates(incentives)
});

export type RederivePriceArgs = {
    manualIncentives: ManualIncentives | undefined;
    offer: Offer;
    offerType: OfferType;
    /** The payment response being reduced. Its `payload.terms` is the price source — see resolveTermForPricing. */
    action: any;
    /** The term selected now, derived once by the caller. */
    currentTermId?: string | null;
    restorationEnabled?: boolean;
};

/**
 * DE760840: re-price the penciled `manualIncentives` snapshot to the term the user has now selected.
 *
 * Prices from the response payload rather than from merged state — see `resolveTermForPricing` for why that
 * distinction is the whole fix.
 *
 * Why the snapshot is rewritten rather than left for the read path to correct: with restoration off,
 * `getAppliedIncentivesByOfferType` returns the snapshot verbatim whenever it exists and only falls back to
 * the term-derived `getOriginalIncentives` when it is absent, so the snapshot shadows the correct per-term
 * amounts. It is also persisted to the saved deal, so a display-only fix would leave the record wrong.
 *
 * The selection comes from the snapshot and only the price comes from the term: with restoration off a
 * term's `appliedIncentives` set is populated only by an explicit apply in Programs and Incentives, so the
 * snapshot is the only record of what the dealer applied and re-deriving the selection from the term would
 * zero the incentive.
 *
 * AC2 (codes from a deselected program) is handled where the program actually changes, in APPLIED_PROGRAM.
 */
export const rederiveManualIncentivesForTerm = ({
    manualIncentives,
    offer,
    offerType,
    action,
    currentTermId,
    restorationEnabled = false
}: RederivePriceArgs): ManualIncentives | undefined => {
    // No snapshot means the read path already derives from the term; there is nothing to correct.
    if (!manualIncentives) {
        return undefined;
    }

    // Only where the snapshot is authoritative. With restoration on, getAppliedIncentivesByOfferType filters
    // program-sourced rows out of the snapshot and rebuilds them from getOriginalIncentives, so re-pricing
    // them changes neither display nor request — it would only rewrite state that gets persisted. Restoration
    // ON behaviour belongs to F292564.
    if (restorationEnabled) {
        return manualIncentives;
    }

    // Only re-price while Auto Select Program is on for this offer type. With it off the deal calculates
    // through /user-program-quotes, meaning the dealer has explicitly opted out of the system choosing
    // programs and incentives — the amount they applied is theirs and must survive a term change untouched.
    // Matches how the checkbox itself defines "on" (AutoSelectProgramScoped: endpoint === programQuotes), and
    // gates on programQuotes rather than "not UPQ" so an unset endpoint is treated as opted out.
    if (selectors.getCalculationEndpointByOfferType(offer, offerType) !== CALC_ENDPOINTS.programQuotes) {
        return manualIncentives;
    }

    const termId = currentTermId ?? offer.penciledInputs?.byOfferType?.[offerType as PenciledInputOfferType]?.term;
    if (!termId) {
        return manualIncentives;
    }

    const { rows: incentives, changed } = repriceRowsFromPriceMap(
        manualIncentives.incentives,
        buildTermIncentivePriceMap(resolveTermForPricing(action, termId))
    );

    if (!changed) {
        return manualIncentives;
    }
    return { ...manualIncentives, incentives, ...sumRebates(incentives) };
};

/**
 * DE760840 (AC1): re-price the applied incentive snapshot when `action` is the payment response settling a
 * term change, and return the penciled inputs untouched otherwise.
 *
 * The gate lives here rather than at the call site so the whole decision is unit-testable in one place.
 */
export const rederiveAppliedIncentivesOnTermSettle = (
    offer: Offer,
    action: any,
    offerType: OfferType,
    currentTermId?: string | null,
    restorationEnabled = false
): PenciledInputsState => {
    const penciledInputs = offer.penciledInputs as PenciledInputsState;
    const inputs = penciledInputs?.byOfferType?.[offerType as PenciledInputOfferType];
    if (!inputs || !shouldRederiveAppliedIncentivesForTerm(action, currentTermId)) {
        return penciledInputs;
    }
    const rederived = rederiveManualIncentivesForTerm({
        manualIncentives: inputs.manualIncentives,
        offer,
        offerType,
        action,
        currentTermId,
        restorationEnabled
    });
    if (rederived === inputs.manualIncentives) {
        return penciledInputs;
    }
    return {
        ...penciledInputs,
        byOfferType: updateOfferTypeWithInputs(penciledInputs.byOfferType, { ...inputs, manualIncentives: rederived }, offerType)
    };
};

/**
 * Freeze the program's rebates into the penciled bag when a good `/program-quotes` response settles, so
 * that on `/user-program-quotes` the applied rebate is the deal's own record rather than re-derived from
 * whichever term record happens to resolve.
 *
 * The only writer. Grid responses, UPQ responses, program-loss responses and term/cash-down edits are all
 * excluded, so nothing but a real program change can alter what the operator applied. Frozen rows are
 * replaced wholesale rather than merged, so a rebate the new program no longer offers cannot survive.
 */
export const freezeProgramRebatesOnGoodProgramQuotes = (
    offer: Offer,
    action: any,
    offerType: OfferType,
    restorationEnabled: boolean
): PenciledInputsState => {
    const penciledInputs = offer.penciledInputs as PenciledInputsState;

    if (
        action?.type !== PAYMENT_RECEIVED ||
        !restorationEnabled ||
        !isUpqRebateLedgerInEffect(offer, offerType) ||
        action.meta?.requestCalculationEndpoint !== CALC_ENDPOINTS.programQuotes ||
        isPaymentGridOriginatedAction(action.meta?.originalAction)
    ) {
        return penciledInputs;
    }

    const inputs = penciledInputs?.byOfferType?.[offerType as PenciledInputOfferType];
    if (!inputs) {
        return penciledInputs;
    }

    // A response that resolved no program is not authoritative about what the program applies.
    if (!selectors.getSelectedTermByOfferType(offer, offerType)?.lenderProgram?.programName) {
        return penciledInputs;
    }

    const programRows = selectors.getProgramRebatesToFreeze(offer, offerType);
    const frozenRows = action.meta.dealerStandardsExcluded
        ? programRows.filter((incentive) => !isProgramStandardIncentive(incentive))
        : programRows;
    const userRows = (inputs.manualIncentives?.incentives ?? []).filter((incentive) => incentive.manual !== false);

    const incentives = [...frozenRows, ...userRows];
    const { dealerCash, totalRebates } = sumRebates(incentives);

    // `ledgerOwned` even when the set is empty: that is how "this program applies no rebate" is expressed.
    // Deleting the bag would read as never-populated and send the read path back to the term record.
    return {
        ...penciledInputs,
        byOfferType: updateOfferTypeWithInputs(
            penciledInputs.byOfferType,
            { ...inputs, manualIncentives: { incentives, dealerCash, totalRebates, ledgerOwned: true } },
            offerType
        )
    };
};

export const dropDealerExcludedStandardsFromBag = (offer: Offer, action: any, offerType: OfferType): PenciledInputsState => {
    const penciledInputs = offer.penciledInputs as PenciledInputsState;
    if (
        !action.meta?.dealerStandardsExcluded ||
        action.meta.isStalePaymentResponse ||
        action.meta.originalAction?.meta?.paymentGrid?.isMainPayment === false
    ) {
        return penciledInputs;
    }
    const inputs = penciledInputs.byOfferType?.[offerType as PenciledInputOfferType];
    const manualIncentives = inputs?.manualIncentives;
    const rows = manualIncentives?.incentives ?? [];
    const isExcludedStandard = selectors.getExcludedStandardPredicate(offer, offerType);
    const incentives = rows.filter((incentive) => !isExcludedStandard(incentive));
    if (incentives.length === rows.length) {
        return penciledInputs;
    }
    const { dealerCash, totalRebates } = sumRebates(incentives);
    return {
        ...penciledInputs,
        byOfferType: updateOfferTypeWithInputs(
            penciledInputs.byOfferType,
            { ...inputs, manualIncentives: { ...manualIncentives, incentives, dealerCash, totalRebates } },
            offerType
        )
    };
};

const isHydratingLoadEndpoint = (offer: Offer, offerType: OfferType): boolean => {
    const initialEndpoint = selectors.getInitialCalculationEndpoint(offer);
    return (
        initialEndpoint === CALC_ENDPOINTS.userProgramQuotes ||
        (initialEndpoint === CALC_ENDPOINTS.programQuotes && isGridIncentiveManagementEnabled(offer, offerType))
    );
};

/**
 * Seed the frozen set for a deal that loads without one. The frozen rows do not survive the DXG save, so
 * on load the rebate survives only in the per-term applied set.
 *
 * Runs on a `/user-program-quotes` load, and with grid incentive management on, on a `/program-quotes`
 * load too: there every later PQ response is grid-originated and never freezes, so without the seed the
 * first UPQ call carries no PQ rebate total (DE780845).
 */
export const hydrateFrozenRebatesOnLoad = (
    offer: Offer,
    offerType: OfferType,
    restorationEnabled: boolean
): PenciledInputsState => {
    const penciledInputs = offer.penciledInputs as PenciledInputsState;

    if (!restorationEnabled || !isUpqRebateLedgerInEffect(offer, offerType) || !isHydratingLoadEndpoint(offer, offerType)) {
        return penciledInputs;
    }

    const inputs = penciledInputs?.byOfferType?.[offerType as PenciledInputOfferType];
    if (!inputs) {
        return penciledInputs;
    }

    const existingRows = inputs.manualIncentives?.incentives ?? [];
    // `ledgerOwned` matters as much as the row test: a deal saved after the operator removed every rebate
    // has an owned bag with no frozen rows, and re-seeding it would undo the removal on every reload.
    // Also what makes this idempotent across the repeated init passes in `mapOfferResponseToOffer`.
    const isBagLedgerOwned = Boolean((inputs.manualIncentives as LedgerAwareManualIncentives | undefined)?.ledgerOwned);
    if (isBagLedgerOwned || existingRows.some(isFrozenProgramRow)) {
        return penciledInputs;
    }

    const frozenRows = selectors.getProgramRebatesToFreeze(offer, offerType);
    if (frozenRows.length === 0) {
        return penciledInputs;
    }

    // The bag's own rows are preserved as-is: a `manual: true` row is the operator's and must stay one.
    const incentives = [...frozenRows, ...existingRows.filter((incentive) => incentive.manual !== false)];
    const { dealerCash, totalRebates } = sumRebates(incentives);

    return {
        ...penciledInputs,
        byOfferType: updateOfferTypeWithInputs(
            penciledInputs.byOfferType,
            { ...inputs, manualIncentives: { incentives, dealerCash, totalRebates, ledgerOwned: true } },
            offerType
        )
    };
};

export const applyPenciledInputsProgram = (
    state: PenciledInputsState,
    action: any,
    offerType: OfferType,
    payload: ProgramPayload,
    preserveUserManualIncentives = false,
    restorationEnabled = false,
    replaceNonConditionalSelection = false,
    keepFrozenProgramRows = false
): PencilInputsByOfferType => {
    const { termId, selectedIncentives } = payload;
    const shouldClearOverrides = action?.meta?.middleware?.shouldClearOverrides || false;
    const penciledInputs = state.byOfferType?.[offerType];
    // Callers that don't intend to touch non-conditionals (e.g. toggling a conditional incentive)
    // send only conditional keys and rely on the previously-applied non-conditionals being carried
    // forward here. A caller that computes the full, authoritative non-conditional set itself
    // (e.g. toggling a non-conditional cash incentive on or off) sets replaceNonConditionalSelection
    // so an explicit removal isn't silently re-added from the stale previous set. See DE759607/US2076398.
    const { allIds, allNames, conditionals, nonConditionals } = aggregatedSelectedIncentives(
        replaceNonConditionalSelection ? [] : penciledInputs?.appliedIncentives?.nonConditionals,
        selectedIncentives
    );
    const updatedInputs: IPencilOfferInputs = {
        ...updateInputWithValue(penciledInputs),
        ...(termId && { term: termId }),
        manualIncentives: preserveUserManualIncentives
            ? keepUserManualIncentives(
                  penciledInputs?.manualIncentives,
                  restorationEnabled,
                  keepFrozenProgramRows ? new Set(allIds.map(Number)) : undefined
              )
            : undefined,
        [SelectedIncentivesNode]: allNames,
        [AppliedIncentiveIdsNode]: allIds,
        [AppliedIncentivesNode]: {
            conditionals,
            nonConditionals
        }
    };

    const updatedPenciledInputs = updateOfferTypeWithInputs(state.byOfferType, updatedInputs, offerType);

    if (shouldClearOverrides) {
        // NOTE: this is a FULL penciled inputs wipe intended for use when toggling off credit decision upon changes on the Shopper Platform side
        return { ...updatedPenciledInputs, ...resetPenciledInputsOverrides(updatedPenciledInputs) };
    }

    return updatedPenciledInputs;
};

/**
 * Picks the base residual to seed for a user-program-quotes handoff, which prices from whatever this returns.
 *
 * Lease prefers the term's program residual, the figure the deal is actually priced at (DE762765). Balloon
 * keeps the term base, because `balloonPaymentAmountOverride` derives from this.
 *
 * @param termEntry The term-scoped entry the user typed, which wins over both term figures. A blank field
 * arrives as `0` rather than absent, so a falsy entry deliberately falls through to the term.
 * @param termData The selected term from the payment response.
 * @param offerType The offer type being seeded; only `LEASE` prefers the program residual.
 * @returns The amount and percent to seed into `baseResidualAmountOverride` and `baseResidualPercentOverride`.
 */
const pickBaseResidualForHandoff = (
    termEntry: ResidualInfo | undefined,
    termData: GenericObject | undefined,
    offerType: OfferType | undefined
): { amount?: number; percent?: number } => {
    const preferProgramResidual = offerType === LEASE;
    const amountFromTerm = preferProgramResidual
        ? termData?.programResidual || termData?.baseResidualDollar
        : termData?.baseResidualDollar;
    const percentFromTerm = preferProgramResidual
        ? termData?.programResidualPercent || termData?.baseResidualPercent
        : termData?.baseResidualPercent;

    return {
        amount: termEntry?.baseResidualAmount || amountFromTerm,
        percent: termEntry?.baseResidualPercent || percentFromTerm
    };
};

export const penciledInputsOverrides = (
    state: PenciledInputsState,
    offer: Offer,
    typeOverride?: OfferType,
    offerType?: OfferType,
    isLeaseResidualTreatmentEnabled = false
): IPencilOfferInputs => {
    const type = typeOverride || offerType;
    const inputs: IPencilOfferInputs = {};
    const selectedTermId = state.byOfferType?.[type!].term;
    const termData = offer.terms.byId?.[selectedTermId!];

    if (!termData || !termHasImplausibleMoneyFactor(termData, type)) {
        inputs.buyRateOverride = termData?.buyRate;
        inputs.sellRateOverride = termData?.sellRate;
        inputs.rateTypeOverride = termData?.rateType;
    }

    if (type === LEASE || type === BALLOON) {
        const months = termData?.months;
        const currentResidualInfoOverrides = state.byOfferType?.[type]?.overridesByTerm?.[months!]?.residualInfo;
        inputs.adjustedResidualAmountOverride =
            currentResidualInfoOverrides?.adjustedResidualAmount || termData?.adjustedResidualValue;
        inputs.adjustedResidualPercentOverride =
            currentResidualInfoOverrides?.adjustedResidualPercent || termData?.adjustedResidualPercent;
        const seededBaseResidual = pickBaseResidualForHandoff(currentResidualInfoOverrides, termData, type);
        inputs.baseResidualAmountOverride = seededBaseResidual.amount;
        inputs.baseResidualPercentOverride = seededBaseResidual.percent;

        inputs.programResidualOverride = currentResidualInfoOverrides?.programResidual || termData?.programResidual;
        inputs.programResidualPercentOverride =
            currentResidualInfoOverrides?.programResidualPercent || termData?.programResidualPercent;

        // if the user already had an adjusted residual type, set it, otherwise stick to dollar
        inputs.adjustedResidualTypeOverride = currentResidualInfoOverrides?.adjustedResidualType || RESIDUAL_DOLLAR;
        inputs.baseResidualTypeOverride =
            currentResidualInfoOverrides?.baseResidualType ||
            (isLeaseResidualTreatmentEnabled && type === LEASE && inputs.baseResidualPercentOverride != null
                ? RESIDUAL_PERCENT
                : RESIDUAL_DOLLAR);

        const acquisitionFee = selectors.getDealerAcquisitionFeeFromTerm(termData);
        if (acquisitionFee != null) {
            const { dealerFeeAmount, capped } = acquisitionFee;
            inputs.acqFeeOverride = dealerFeeAmount;
            inputs.acqFeeUpFrontOverride = !capped;
        }
        inputs.mileageChargeOverride = termData?.excessMileageCharge ?? (0 as number);
        inputs.securityDepositOverride = termData?.securityDeposit ?? 0;
        inputs.maxResidualizedMsrp = termData?.maxResidualizedMsrp;
        inputs.maxResidualizedMsrpApplied = termData?.maxResidualizedMsrpApplied;
    }

    if (type === BALLOON) {
        inputs.balloonPaymentAmountOverride = inputs.baseResidualAmountOverride;
    }

    return inputs;
};
